import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { testimonials } from '@/lib/testimonials';
import {
    BookOpen, Users, Video, PhoneCall, FileText, Lightbulb,
    Wrench, Rocket, CheckCircle, Star, TrendingUp, XCircle, Calendar,
    Presentation, Brain, Target, BarChart, DollarSign, Shield, Coffee, User, ArrowUp, Menu, X, ChevronDown, HelpCircle
} from 'lucide-react';

interface Testimonial {
    quote: string;
    author: string;
    role: string;
    image: string;
}

const SectionDivider = ({ className }: { className?: string }) => (
    <div className={cn("absolute left-0 w-full h-16 md:h-24 lg:h-32 overflow-hidden", className)}>
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none" className="h-full w-full fill-current">
            <path d="M0,0 L1440,80L1440,120L0,120Z" />
        </svg>
    </div>
);

const RealTestimonialCard = ({ testimonial }: { testimonial: Testimonial }) => {
    return (
        <Card className="testimonial-card bg-white dark:bg-gray-800/50 p-6 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl border border-gray-100 dark:border-gray-700/50 relative overflow-hidden group h-full flex flex-col">
            <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-bl-full opacity-50 group-hover:opacity-100 transition-opacity"></div>
            <CardHeader className="flex items-center space-x-4 p-0 mb-4 relative z-10">
                {testimonial.image ? (
                    <img
                        src={testimonial.image}
                        alt={testimonial.author}
                        className="w-14 h-14 rounded-full object-cover border-2 border-indigo-200 dark:border-indigo-700"
                    />
                ) : (
                    <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center border-2 border-indigo-200 dark:border-indigo-700">
                        <User className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                    </div>
                )}
                <div className='flex-1'>
                    <h4 className="font-semibold text-lg text-gray-800 dark:text-white">{testimonial.author}</h4>
                    <p className="text-indigo-600 dark:text-indigo-400 text-sm">{testimonial.role}</p>
                </div>
            </CardHeader>
            <CardContent className="p-0 flex-grow relative z-10">
                <p
                    className="text-gray-600 dark:text-gray-300 italic leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: `"${testimonial.quote}"` }}
                />
            </CardContent>
        </Card>
    );
};

export default function OwnTheAgentPage() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    const observer = useRef<IntersectionObserver | null>(null);

    const handleScroll = useCallback(() => {
        const scrollPosition = window.scrollY;
        setIsScrolled(scrollPosition > 50);
        setShowBackToTop(scrollPosition > 300);

        // Basic section highlighting (can be improved with IntersectionObserver)
        const sections = ['program', 'opportunity', 'investment', 'faq', 'application-form'];
        let currentSection = '';
        for (const id of sections) {
            const element = document.getElementById(id);
            if (element && element.offsetTop <= scrollPosition + 100) {
                currentSection = id;
            }
        }
        setActiveSection(currentSection);

    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check

        // Fade-in animation observer
        const elementsToAnimate = document.querySelectorAll('.scroll-animate');
        observer.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('fade-in-up-visible');
                    observer.current?.unobserve(entry.target); // Optional: stop observing once visible
                }
            });
        }, { threshold: 0.1 });

        elementsToAnimate.forEach(el => observer.current?.observe(el));

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.current?.disconnect();
        };
    }, [handleScroll]);

    const scrollToSection = (id: string, offset = 80) => {
        const element = document.getElementById(id);
        if (element) {
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
        setIsMobileMenuOpen(false); // Close mobile menu on navigation
    };

    const navLinks = [
        { id: 'program', label: 'Program' },
        { id: 'opportunity', label: 'Opportunity' },
        { id: 'investment', label: 'Investment' },
        { id: 'faq', label: 'FAQ' },
    ];

    return (
        <div className="min-h-screen bg-white dark:bg-gray-950 font-sans antialiased relative">


            {/* Floating Mobile CTA */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-gray-950/90 backdrop-blur-sm p-3 border-t border-gray-200 dark:border-gray-800 z-40 shadow-[0_-4px_12px_-1px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_12px_-1px_rgba(0,0,0,0.4)]">
                <div className="container mx-auto flex justify-between items-center gap-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Limited spots remaining!</span>
                    <Button
                        asChild
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 dark:text-white transition-colors flex-shrink-0 px-5 py-2.5"
                    >
                        <Link to="/ota-application">Apply Now</Link>
                    </Button>
                </div>
            </div>

            {/* Hero Section */}
            <div className="relative bg-gradient-to-br from-indigo-700 via-blue-800 to-purple-900 text-white pt-24 md:pt-32 pb-40 md:pb-48 overflow-hidden">
                {/* Subtle Background Pattern */}
                <div className="absolute inset-0 opacity-10 dark:opacity-5 bg-[url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2720%27 height=%2720%27 viewBox=%270 0 20 20%27%3E%3Cg fill=%27%23ffffff%27%3E%3Cpath fill-opacity=%270.4%27 d=%27M0 0 H 10 V 10 H 0 Z%27/%3E%3C/g%3E%3C/svg%3E')]"></div>
                <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px] z-10 text-center">
                    {/* Badge kept for potential future use, but hidden for now based on new copy */}
                    {/* <Badge variant="secondary" className="mb-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-4 py-1">
                        Limited to 15 Participants
                    </Badge> */}
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 drop-shadow-lg leading-tight scroll-animate">
                        For the friendly, ambitious geeks who automatically think<br /> "I could build something for that"
                    </h1>
                    <p className="text-xl md:text-2xl mb-10 text-white/90 max-w-3xl mx-auto scroll-animate" style={{ animationDelay: '0.2s' }}>
                        Own the Agent™ brings together 15 practical innovators who see business
                        challenges as the perfect excuse to create amazing AI tools.
                    </p>
                    <p className="text-xl md:text-2xl mb-10 text-white/90 max-w-3xl mx-auto scroll-animate" style={{ animationDelay: '0.4s' }}>
                        No more solo tinkering. Let's build remarkable things together.
                    </p>
                    <div className="flex flex-col items-center justify-center gap-4 mb-8 max-w-md mx-auto scroll-animate" style={{ animationDelay: '0.6s' }}>
                        <Button
                            asChild
                            size="lg"
                            className="w-full md:w-auto px-10 py-9 bg-white text-indigo-700 font-semibold hover:bg-white/90 transition-colors duration-200 shadow-xl hover:shadow-2xl text-lg transform hover:scale-105"
                        >
                            <Link to="/ota-application">
                                → Do you qualify?<br /> (limited to 15 people)
                            </Link>
                        </Button>
                    </div>
                    {/* Optional: Add back the 'Applications close...' text if desired */}
                    {/* <p className="text-white/70 text-sm scroll-animate" style={{ animationDelay: '0.8s' }}>Applications close when all spots are filled</p> */}
                </div>
                {/* Angled Section Divider */}
                <div className="absolute bottom-0 left-0 w-full h-24 md:h-32 lg:h-40 text-white dark:text-gray-950 overflow-hidden">
                    <svg viewBox="0 0 1440 100" preserveAspectRatio="none" className="h-full w-full fill-current">
                        <path d="M0,100 C480,0 960,0 1440,100 L1440,100 L0,100 Z" />
                    </svg>
                </div>
            </div>

            {/* Venn Diagram & Intro Section - REVISED LAYOUT -> Power Triangle Section */}
            <section className="bg-white dark:bg-gray-950 relative z-10 -mt-24 md:-mt-32 lg:-mt-40 pb-16 md:pb-24 pt-8 md:pt-12">
                {/* Revert to 2-column grid and add back image */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px]">
                    <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
                        {/* Left Column: Text */}
                        <div className="text-center md:text-left scroll-animate">
                            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900 dark:text-white">
                                THE GENIUS TRIFECTA
                            </h2>
                            <p className="text-lg lg:text-xl text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                                Own the Agent™ thrives at the sweet spot where curious minds create real-world magic—where Business Strategy meets AI Wizardry meets Digital Marketing Mastery.
                            </p>
                            <div className="space-y-4 text-lg lg:text-xl text-gray-700 dark:text-gray-300 font-medium mb-6">
                                <p><strong>This isn't about hype. It's about helping humans do their best work.</strong></p>
                                <p><strong>This isn't about abstract promises. It's about actual progress you can measure.</strong></p>
                                <p><strong>This isn't about "someday technology." It's about Monday morning breakthroughs.</strong></p>
                            </div>
                            <p className="text-lg lg:text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                                For the friendly ambitious geeks who know there's a better way to work—and want to build it themselves. Join the 15 who are quietly revolutionizing their businesses while others are still reading about AI on Twitter.
                            </p>
                        </div>
                        {/* Right Column: Remove Card wrapper, keep image */}
                        <div className="flex justify-center md:justify-end scroll-animate" style={{ animationDelay: '0.2s' }}>
                            {/* Card component removed */}
                            <img
                                src="/images/venn.png"
                                alt="The intersection of Business, AI, and Digital Marketing"
                                // Apply max-width directly to image if needed, similar to card's previous constraint 
                                className="w-full max-w-md lg:max-w-lg h-auto object-contain block"
                            />
                            {/* </Card> Removed */}
                        </div>
                    </div>
                </div>
            </section>



            {/* Fit Section -> Ideal Member Profile Section */}
            <section className="bg-white dark:bg-gray-950 py-16 md:py-24">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1000px]">
                    <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-white scroll-animate">IS THIS YOUR COMPETITIVE EDGE?</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                        <div className="scroll-animate" style={{ animationDelay: '0.1s' }}>
                            <h3 className="text-2xl font-bold mb-6 text-green-700 dark:text-green-400 flex items-center">
                                <CheckCircle className="h-7 w-7 mr-3 flex-shrink-0" /> You belong in this elite group if:
                            </h3>
                            <ul className="space-y-4 text-gray-700 dark:text-gray-300 text-lg">
                                <li className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                                    <span>You run a business that's ready to <strong className="text-gray-900 dark:text-white">leap ahead</strong>—not inch forward</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                                    <span>You're done with AI theory and <strong className="text-gray-900 dark:text-white">hungry for execution</strong></span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                                    <span>You value <strong className="text-gray-900 dark:text-white">decisive implementation</strong> over endless discussion</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                                    <span>You're willing to <strong className="text-gray-900 dark:text-white">dedicate time between sessions</strong> to deploy what you learn</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                                    <span>You see business challenges as <strong className="text-gray-900 dark:text-white">profit opportunities</strong> waiting for the right tool</span>
                                </li>
                            </ul>
                        </div>
                        <div className="scroll-animate" style={{ animationDelay: '0.2s' }}>
                            <h3 className="text-2xl font-bold mb-6 text-red-700 dark:text-red-400 flex items-center">
                                <XCircle className="h-7 w-7 mr-3 flex-shrink-0" /> This is NOT for you if:
                            </h3>
                            <ul className="space-y-4 text-gray-700 dark:text-gray-300 text-lg">
                                <li className="flex items-start gap-3">
                                    <XCircle className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                                    <span>You want <strong className="text-gray-900 dark:text-white">passive learning</strong></span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <XCircle className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                                    <span>You <strong className="text-gray-900 dark:text-white">can't commit</strong> to implementation time</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <XCircle className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                                    <span>You expect AI to work magic <strong className="text-gray-900 dark:text-white">without your strategic direction</strong></span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>


            {/* Testimonials Section - Reframed */}
            <section className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950 py-16 md:py-24 relative">
                {/* Wavy Top Divider */}
                <div className="absolute top-0 left-0 w-full h-16 md:h-24 text-white dark:text-gray-950 transform rotate-180">
                    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="h-full w-full fill-current">
                        <path d="M0,80 C480,0 960,0 1440,80 L1440,80 L0,80 Z" />
                    </svg>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px] relative z-10 pt-16">
                    <div className="text-center max-w-3xl mx-auto mb-16 scroll-animate">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">REAL PEOPLE, REAL "AHA!" MOMENTS</h2>
                        <p className="text-xl text-muted-foreground">
                            No manufactured success stories here. Just fellow tech enthusiasts who've worked with Mike and had those delightful "why didn't I do this sooner?" moments:
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Add headers above each testimonial */}
                        <div className="scroll-animate" style={{ animationDelay: `0.1s` }}>
                            <h3 className="text-lg font-semibold text-center mb-3 text-gray-700 dark:text-gray-300">"From Script-Phobic to Data Detective"</h3>
                            <RealTestimonialCard testimonial={testimonials[0]} /> {/* Assuming Jyll is first */}
                        </div>
                        <div className="scroll-animate" style={{ animationDelay: `0.2s` }}>
                            <h3 className="text-lg font-semibold text-center mb-3 text-gray-700 dark:text-gray-300">"Making the Complex Beautifully Simple"</h3>
                            <RealTestimonialCard testimonial={testimonials[1]} /> {/* Assuming Alfred is second */}
                        </div>
                        <div className="scroll-animate" style={{ animationDelay: `0.3s` }}>
                            <h3 className="text-lg font-semibold text-center mb-3 text-gray-700 dark:text-gray-300">"The Joy of Escaping Google's Clunky Interface"</h3>
                            <RealTestimonialCard testimonial={testimonials[2]} /> {/* Assuming Stacey is third */}
                        </div>
                    </div>
                </div>
                {/* Wavy Bottom Divider */}
                <div className="absolute bottom-0 left-0 w-full h-16 md:h-24 text-white dark:text-gray-950">
                    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="h-full w-full fill-current">
                        <path d="M0,0 C480,80 960,80 1440,0 L1440,80 L0,80 Z" />
                    </svg>
                </div>
            </section>

            {/* 7. Investment Options - Revert Design/Data, Keep Framing */}
            <div className="bg-gray-50 dark:bg-gray-900 py-16 md:py-24" id="investment">
                <div className="container mx-auto px-4 max-w-[1400px]">
                    <div className="text-center max-w-3xl mx-auto mb-12 scroll-animate">
                        <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">YOUR ADVENTURE PATHS</h2>
                        <p className="text-xl text-muted-foreground">
                            Choose the journey that fits your ambitions (and calendar)
                        </p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto scroll-animate" style={{ animationDelay: '0.2s' }}>
                        {/* Annual Membership - Ensure flex layout */}
                        <Card className="flex flex-col h-full border-2 border-indigo-500 dark:border-indigo-400 bg-white dark:bg-gray-900 shadow-xl overflow-hidden relative">
                            <div className="absolute -right-10 top-6 rotate-45 bg-indigo-600 text-white px-10 py-1 text-sm font-bold">BEST VALUE</div>
                            <CardHeader className="bg-indigo-50 dark:bg-indigo-950/30 border-b border-indigo-100 dark:border-indigo-800">
                                <CardTitle className="text-center space-y-2 pt-6">
                                    <div className="text-sm text-indigo-600 dark:text-indigo-400 font-semibold mb-1">Annual Membership: The Full Expedition</div>
                                    <div className="text-3xl font-bold text-indigo-700 dark:text-indigo-300">
                                        €11,999
                                    </div>
                                    <div className="text-sm text-muted-foreground">(€1,000/month equivalent)</div>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 text-gray-900 dark:text-white flex-grow">
                                <ul className="space-y-3">
                                    {[
                                        "Full year of implementation support",
                                        "17 one-on-one strategy sessions",
                                        "52 group implementation calls",
                                        "Priority access to all new tools and strategies",
                                        "Ticket to exclusive annual retreat",
                                        "First right of refusal for future retreats",
                                        "Save €15,601 compared to monthly option"
                                    ].map((feature, index) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /> <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Calendar className="h-5 w-5 text-indigo-600" />
                                        <span className="font-semibold">Annual Retreat Included</span>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Join our exclusive annual meeting alternating between Australia and Europe. Limited to 15-20 participants for an intimate, transformative experience.
                                    </p>
                                </div>
                            </CardContent>
                            <CardFooter className="mt-auto bg-indigo-50 dark:bg-indigo-950/30 border-t border-indigo-100 dark:border-indigo-800 flex justify-center p-6">
                                <Button asChild className="w-full bg-indigo-600 hover:bg-indigo-700 py-5 text-lg">
                                    <Link to="/ota-application">Apply Now</Link>
                                </Button>
                            </CardFooter>
                        </Card>

                        {/* Six-Month Accelerator - Apply flex layout and purple theme */}
                        <Card className="flex flex-col h-full bg-purple-50 dark:bg-purple-950/30 shadow-lg overflow-hidden">
                            <CardHeader className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                                <CardTitle className="text-center space-y-2 pt-6">
                                    <div className="text-sm text-purple-600 dark:text-purple-400 font-semibold mb-1">Six-Month Accelerator: The Perfect Jumpstart</div>
                                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                                        €8,999
                                    </div>
                                    <div className="text-sm text-muted-foreground">(€1,500/month equivalent)</div>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 text-gray-900 dark:text-white flex-grow">
                                <ul className="space-y-3">
                                    {[
                                        "Six months of intensive implementation",
                                        "8 one-on-one strategy sessions",
                                        "26 group implementation calls",
                                        "Priority access to all new tools and strategies",
                                        "Save €4,801 compared to monthly option"
                                    ].map((feature, index) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /> <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-6 p-4 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Calendar className="h-5 w-5 text-gray-600" />
                                        <span className="font-semibold">Annual Retreat Option</span>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Option to purchase tickets to our exclusive annual retreat (additional €2,500 per event).
                                    </p>
                                </div>
                            </CardContent>
                            <CardFooter className="mt-auto bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex justify-center p-6">
                                <Button asChild className="w-full bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-500 dark:hover:bg-purple-600 dark:text-white py-5 text-lg">
                                    <Link to="/ota-application">Apply Now</Link>
                                </Button>
                            </CardFooter>
                        </Card>

                        {/* Monthly Implementation - Ensure flex layout */}
                        <Card className="flex flex-col h-full bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
                            <CardHeader className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                                <CardTitle className="text-center space-y-2 pt-6">
                                    <div className="text-sm text-gray-700 dark:text-gray-300 font-semibold mb-1">Monthly Implementation: Test the Waters</div>
                                    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                                        €2,499
                                    </div>
                                    <div className="text-sm text-muted-foreground">per month</div>
                                    <div className="text-xs text-muted-foreground">(3-month minimum commitment)</div>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 text-gray-900 dark:text-white flex-grow">
                                <ul className="space-y-3">
                                    {[
                                        "Flexible implementation timeline",
                                        "One-on-one strategy session every 3 weeks",
                                        "Weekly group implementation calls",
                                        "Priority access to all new tools and strategies",
                                        "Minimum initial investment: €7,497"
                                    ].map((feature, index) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /> <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Calendar className="h-5 w-5 text-gray-600" />
                                        <span className="font-semibold">Annual Retreat Option</span>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Option to purchase tickets to our exclusive annual retreat (additional €5,000 per event).
                                    </p>
                                </div>
                            </CardContent>
                            <CardFooter className="mt-auto bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex justify-center p-6">
                                <Button asChild variant="outline" className="w-full border-indigo-300 text-indigo-600 hover:bg-indigo-100/50 hover:text-indigo-700 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-200 py-5 text-lg">
                                    <Link to="/ota-application">Apply Now</Link>
                                </Button>
                            </CardFooter>
                        </Card>
                    </div>
                    <div className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400 scroll-animate" style={{ animationDelay: '0.4s' }}>
                        <p>Build the Agent Workshop Attendees: Apply your €500 credit to any option</p>
                    </div>
                </div>
            </div>

            {/* Application Process Section */}
            <section className="bg-white dark:bg-gray-950 py-16 md:py-24" id="application-form">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px]">
                    <div className="text-center max-w-3xl mx-auto mb-16 scroll-animate">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">FINDING YOUR PEOPLE</h2>
                        <p className="text-xl text-muted-foreground">
                            We keep this group intentionally small because magic happens when the right minds connect. No lengthy applications or corporate hoops—just a straightforward process to make sure we're the perfect fit for each other:
                        </p>
                    </div>
                    <div className="max-w-5xl mx-auto scroll-animate" style={{ animationDelay: '0.2s' }}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
                            {/* Connecting Lines */}
                            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-indigo-200 dark:bg-indigo-800/50 -translate-y-1/2 z-0" style={{ width: 'calc(100% - 10rem)', margin: '0 5rem' }}></div>

                            {[
                                { step: "1", title: "Discovery Application", description: "Submit your business context, challenges, and AI implementation goals." },
                                { step: "2", title: "Strategic Assessment", description: "Selected applicants engage in a one-on-one qualification conversation to evaluate potential impact." },
                                { step: "3", title: "Mutual Commitment Decision", description: "Together, we determine if this partnership represents the optimal path forward for your specific situation." },
                                { step: "4", title: "Implementation Commencement", description: "Accepted members select their investment level and begin their structured transformation journey." }
                            ].map((step, index) => (
                                <div key={index} className="relative text-center z-10">
                                    <div className="relative bg-white dark:bg-gray-800/50 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700/50 transition-shadow hover:shadow-xl h-full flex flex-col items-center">
                                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 flex items-center justify-center mx-auto mb-5 border-2 border-indigo-200 dark:border-indigo-700 shadow-inner">
                                            <span className="text-indigo-600 dark:text-indigo-300 font-bold text-2xl">{step.step}</span>
                                        </div>
                                        <h4 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">{step.title}</h4>
                                        <p className="text-sm text-muted-foreground flex-grow">{step.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-16 text-center scroll-animate" style={{ animationDelay: '0.4s' }}>
                            <p className="text-2xl font-bold mb-8 text-gray-900 dark:text-white">Ready to truly Own the Agent™ in your business?</p>
                            <Button
                                asChild
                                size="lg"
                                className="bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 dark:from-indigo-500 dark:to-purple-600 dark:hover:from-indigo-600 dark:hover:to-purple-700 text-white py-4 px-12 text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                                style={{ animationDelay: '0.3s' }}
                            >
                                <Link to="/ota-application">Begin Your Application Process</Link>
                            </Button>
                            <p className="mt-6 text-gray-600 dark:text-gray-400 text-sm">
                                Our selective process maintains the program's integrity and ensures every participant receives the focused attention required for meaningful implementation.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section - Enhance */}
            <section className="bg-gray-50 dark:bg-gray-900 py-16 md:py-24 relative" id="faq">
                {/* Wavy Top Divider */}
                <div className="absolute top-0 left-0 w-full h-16 md:h-24 text-white dark:text-gray-950 transform rotate-180">
                    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="h-full w-full fill-current">
                        <path d="M0,80 C480,0 960,0 1440,80 L1440,80 L0,80 Z" />
                    </svg>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[900px] relative z-10 pt-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-white scroll-animate">CURIOUS MINDS WANT TO KNOW...</h2>
                    <p className="text-center text-lg text-gray-600 dark:text-gray-400 mb-10 scroll-animate" style={{ animationDelay: '0.1s' }}>(The questions that pop up when clever people are considering joining us)</p>
                    <Accordion type="single" collapsible className="space-y-5 scroll-animate" style={{ animationDelay: '0.2s' }}>
                        {[
                            {
                                question: "HOW QUICKLY WILL I SEE ROI?",
                                answer: `Most members have their first "this just paid for itself" moment within 2-3 weeks. It's not just about money saved—it's about those delightful moments when you realize you've automated something that used to eat hours of your week. That feeling when you think "I'll never have to do that tedious task again" is almost better than the financial return (almost).`
                            },
                            {
                                question: "How is Own the Agent™ different from Build the Agent?",
                                answer: `Build the Agent is where you tinker and create something for yourself. Own the Agent™ is where you transform your entire business.\n\nThis is about creating AI tools that scale beyond just you—tools for your team, your clients, maybe even tools you can sell. Plus, our one-on-one sessions provide something invaluable: gentle but persistent accountability. I'll be there asking "how did that implementation go?" and helping you overcome any obstacles. It's amazing how much more gets done when someone else is expecting to see your progress.`
                            },
                            {
                                question: "I'm already stretched thin. How much time will this really take?",
                                answer: `Let me frame this differently: Are you willing to invest 5% of your resources now to protect 100% of your business?\n\nBecause that's the choice we're facing. Invest a manageable amount of time and energy implementing AI throughout your business now, or risk becoming completely obsolete within 2-3 years. The most common feedback I get is "I wish I'd done this sooner because it's actually giving me time back, not taking it away."`
                            },
                            {
                                question: "What specific results can I anticipate?",
                                answer: `Beyond the 2-4 significant AI implementations most members complete in their first six months, you'll develop something even more valuable: a strategic approach to identifying which AI opportunities to pursue first. Instead of scattered efforts, you'll have a framework for continuous improvement that compounds over time. Members typically reclaim 10+ hours weekly within the first month—hours they reinvest in higher-value work.`
                            },
                            {
                                question: "Is this relevant outside of digital marketing agencies?",
                                answer: `Absolutely. While my background includes digital marketing expertise, the principles of strategic AI integration work anywhere decisions are made, data is collected, or repetitive tasks exist (which is... everywhere). Current members run service businesses, SaaS companies, e-commerce stores, professional practices, and more.`
                            },
                            {
                                question: "What's the community like?",
                                answer: `Deliberately small. With only 15 members total, you'll genuinely get to know everyone. Unlike massive courses where you're just another avatar, this group becomes a trusted circle. Members often end up helping each other, collaborating on projects, and forming lasting professional friendships.\n\nThere's a waitlist if someone leaves (though that rarely happens), so the group stays consistent enough to build real relationships but fresh enough to bring in new perspectives.`
                            },
                            {
                                question: "What happens after my initial commitment term?",
                                answer: `Most members continue as their AI strategy evolves from specific implementations to broader business transformation. You'll have the option to renew (often at preferential rates), but there's no pressure. Some graduate when they've built enough momentum to continue independently, and that's considered a success too.`
                            },
                            {
                                question: "What if life gets crazy and I miss sessions?",
                                answer: `We're all running businesses here—I get it. Group calls are recorded so you can catch up. One-on-one sessions can be rescheduled with reasonable notice. And the private community provides continuous support between scheduled interactions. The program is designed for busy professionals who occasionally need flexibility.`
                            },
                            {
                                question: "About that annual retreat...tell me more?",
                                answer: `It's the in-person highlight of the year—an intimate gathering (just 15-20 peers) alternating between Australia and Europe. We dive deep into strategy, work through challenges together, and often make breakthroughs that would take months otherwise.\n\nAnnual members get one ticket included. Others can purchase tickets (€5k, subject to availability) for this high-impact strategic event. It's optimal for big-picture thinking away from daily distractions.`
                            },
                            {
                                question: "Can my team join the program with me?",
                                answer: `Absolutely! This is your AI journey, and we want it to create real change in your business – that often means getting key team members involved.\n\nFor one-on-one strategy sessions, bring as many team members as you'd like. These are your calls, and having the right people in the room can accelerate implementation.\n\nFor group calls, we ask that you limit attendance to yourself plus one team member at most. We're keeping the group intentionally small (that's where the magic happens), so we need to maintain that intimate environment.\n\nMany members find a great rhythm by attending group sessions themselves and then having focused implementation sessions with their team afterward. It's the best of both worlds – you get the insights and connections, and your team gets clear direction.`
                            },
                        ].map((faq, index) => (
                            <AccordionItem
                                key={index}
                                value={`item-${index}`}
                                className="border border-gray-200 dark:border-gray-700/50 rounded-lg overflow-hidden bg-white dark:bg-gray-800/50 shadow-sm transition-shadow hover:shadow-md"
                            >
                                <AccordionTrigger
                                    className="text-left font-semibold text-lg px-6 py-4 text-gray-900 dark:text-white hover:no-underline flex justify-between items-center w-full group"
                                    aria-controls={`content-item-${index}`}
                                    id={`header-item-${index}`}
                                >
                                    <span>{faq.question}</span>
                                </AccordionTrigger>
                                <AccordionContent
                                    className="text-gray-600 dark:text-gray-400 text-base pt-0 px-6 pb-5 data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden whitespace-pre-line"
                                    id={`content-item-${index}`}
                                    aria-labelledby={`header-item-${index}`}
                                >
                                    {faq.answer}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Final CTA Section - Reframe */}
            <section className="bg-gradient-to-r from-indigo-700 via-blue-800 to-purple-900 text-white py-16 md:py-24">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[900px] text-center">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 scroll-animate">BUILDERS & OPTIMIZERS WANTED</h2>
                    <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-white/90 scroll-animate" style={{ animationDelay: '0.1s' }}>
                        Every industry has two types of professionals: those who adapt to change when forced, and those who gleefully build the future with their own hands.
                    </p>
                    <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-white/90 scroll-animate" style={{ animationDelay: '0.2s' }}>
                        This group is for the builders—the ones who see AI not as a buzzword but as the perfect tool they've been waiting for.
                    </p>
                    <p className="text-3xl font-bold mb-10 max-w-2xl mx-auto text-white scroll-animate" style={{ animationDelay: '0.3s' }}>
                        Is that you? (We think it might be.)
                    </p>
                    <Button
                        asChild
                        size="lg"
                        className="bg-white text-indigo-700 font-semibold hover:bg-white/90 transition-colors duration-200 shadow-xl hover:shadow-2xl text-lg transform hover:scale-105 px-12 py-4 scroll-animate"
                        style={{ animationDelay: '0.4s' }}
                    >
                        <Link to="/ota-application">→ LET'S FIND OUT TOGETHER</Link>
                    </Button>
                    <div className="mt-8 scroll-animate" style={{ animationDelay: '0.5s' }}>
                        <p className="text-white/80 text-sm">
                            (Conversation first, commitment second. Promise.)
                        </p>
                        <p className="text-white/80 text-sm mt-2">
                            <Star className="h-4 w-4 inline mr-1 mb-0.5 text-yellow-300" /> Previous Build the Agent participants: Remember to apply your discount coupon.
                        </p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-400 py-12 md:py-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px]">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className='md:col-span-2'>
                            <h3 className="text-xl font-bold mb-4 text-white">Own the Agent™</h3>
                            <p className="text-sm leading-relaxed mb-4">Transform your business through strategic AI implementation with guidance from Mike Rhodes.</p>
                            <p className="text-xs">&copy; {new Date().getFullYear()} Own the Agent™ by Mike Rhodes. E&OE. All rights reserved.</p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-4 text-white">Quick Links</h3>
                            <ul className="space-y-2 text-sm">
                                <li><a href="/buy" className="hover:text-white transition-colors duration-200">All Products</a></li>
                                <li><a href="/build" className="hover:text-white transition-colors duration-200">Build the Agent Workshop</a></li>
                                <li><a href="/mastery" className="hover:text-white transition-colors duration-200">Scripts & Sheets Mastery</a></li>
                                <li><a href="/community" className="hover:text-white transition-colors duration-200">From Ads to AI Community</a></li>
                                <li><a href="#" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }} className="hover:text-white transition-colors duration-200">FAQ</a></li>

                            </ul>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-4 text-white">Legal</h3>
                            <ul className="space-y-2 text-sm">
                                <li><a href="/terms" className="hover:text-white transition-colors duration-200">Terms and Conditions</a></li>
                                <li><a href="/privacy" className="hover:text-white transition-colors duration-200">Privacy Policy</a></li>
                            </ul>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Back to Top Button */}
            <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className={cn(
                    "fixed bottom-6 right-6 lg:bottom-8 lg:right-8 p-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-full shadow-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all duration-300 z-40",
                    showBackToTop ? "opacity-100 scale-100" : "opacity-0 scale-0"
                )}
                aria-label="Back to top"
            >
                <ArrowUp className="h-6 w-6" />
            </button>


            {/* Global styles for animations */}
            <style>{`
                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .scroll-animate {
                    opacity: 0;
                    transform: translateY(20px);
                    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
                }
                .fade-in-up-visible {
                    opacity: 1;
                    transform: translateY(0);
                }
            `}</style>
        </div>
    );
}